import { useState, useEffect, useMemo, useRef } from 'react';
import { Package, Plus, Search, X, Scan, FileSpreadsheet, Printer, Loader2, ArrowRight, AlertCircle, CheckCircle2, XCircle, Pencil, MoveRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../lib/api';
import { normalizeBarcode } from '../lib/utils';
import { DepotLockToggle } from '../components/DepotLockToggle';

export const DepozitV2 = () => {
    const [loading, setLoading] = useState(true);
    const [pallets, setPallets] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    
    // Global search state
    const [globalSearchQuery, setGlobalSearchQuery] = useState('');
    const [globalSearchResults, setGlobalSearchResults] = useState([]);
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [highlightedBarcode, setHighlightedBarcode] = useState(null);
    
    // Edit name state
    const [editingBarcode, setEditingBarcode] = useState(null);
    const [editNameValue, setEditNameValue] = useState('');
    const editNameRef = useRef(null);
    
    // Edit price state
    const [editingPriceBarcode, setEditingPriceBarcode] = useState(null);
    const [editPriceValue, setEditPriceValue] = useState('');
    const editPriceRef = useRef(null);
    
    // Confirmation dialog for high values
    const [confirmHighValue, setConfirmHighValue] = useState(null);
    
    // Move items state
    const [showMoveDialog, setShowMoveDialog] = useState(false);
    const [moveTargetPallet, setMoveTargetPallet] = useState('');
    const [moveLoading, setMoveLoading] = useState(false);
    const [selectedMoveItems, setSelectedMoveItems] = useState([]); // empty = all
    const searchRef = useRef(null);
    
    // Modal state
    const [selectedPallet, setSelectedPallet] = useState(null);
    const [showPalletModal, setShowPalletModal] = useState(false);
    const [palletItems, setPalletItems] = useState([]);
    const [loadingItems, setLoadingItems] = useState(false);
    
    // Double scan state
    const [scan1, setScan1] = useState('');
    const [scan2, setScan2] = useState('');
    const [scanDescription, setScanDescription] = useState('');
    const [scanQty, setScanQty] = useState(1);
    const [fetchedPrice, setFetchedPrice] = useState(0);
    const [priceSource, setPriceSource] = useState('');
    const [manualPrice, setManualPrice] = useState('');
    const [scanning, setScanning] = useState(false);
    
    // Validation states
    const [scanError, setScanError] = useState('');
    const [shortCodeWarning, setShortCodeWarning] = useState(false);
    const [scanValidated, setScanValidated] = useState(false);
    const [validatedBorder, setValidatedBorder] = useState(false);
    const [existingProduct, setExistingProduct] = useState(null);
    const [descriptionLocked, setDescriptionLocked] = useState(false);
    
    // Refs for auto-focus
    const scan1Ref = useRef(null);
    const scan2Ref = useRef(null);
    const descriptionRef = useRef(null);
    const quantityRef = useRef(null);
    const priceRef = useRef(null);

    useEffect(() => {
        loadPallets();
    }, []);
    
    // Click outside to close search results
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowSearchResults(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    
    // Global search across all pallets - search by barcode AND name
    useEffect(() => {
        if (!globalSearchQuery.trim()) {
            setGlobalSearchResults([]);
            setShowSearchResults(false);
            return;
        }
        
        const query = globalSearchQuery.toLowerCase().trim();
        const results = [];
        const productMap = new Map(); // barcode -> {name, locations: [{palletId, palletNumber, quantity}]}
        
        pallets.forEach(pallet => {
            const palletNumber = pallet.pallet_id || pallet.number;
            (pallet.boxes || []).forEach(box => {
                (box.items || []).forEach(item => {
                    const barcode = (item.barcode || '').toUpperCase();
                    const name = (item.name || '').toLowerCase();
                    const qty = item.quantity || 0;
                    
                    // Search in both barcode and name
                    if (barcode.toLowerCase().includes(query) || name.includes(query)) {
                        if (!productMap.has(barcode)) {
                            productMap.set(barcode, {
                                barcode: barcode,
                                name: item.name || barcode,
                                locations: []
                            });
                        }
                        // Check if this pallet already has this product
                        const existing = productMap.get(barcode);
                        const existingLocation = existing.locations.find(l => l.palletNumber === palletNumber);
                        if (existingLocation) {
                            existingLocation.quantity += qty;
                        } else {
                            existing.locations.push({
                                palletId: pallet.id,
                                palletNumber: palletNumber,
                                quantity: qty,
                                pallet: pallet
                            });
                        }
                    }
                });
            });
        });
        
        // Convert map to array
        productMap.forEach((product) => {
            results.push(product);
        });
        
        // Sort by name
        results.sort((a, b) => a.name.localeCompare(b.name, 'ro'));
        
        setGlobalSearchResults(results);
        setShowSearchResults(results.length > 0);
    }, [globalSearchQuery, pallets]);
    
    // Navigate to pallet and highlight product
    const navigateToPallet = (pallet, barcode) => {
        setShowSearchResults(false);
        setGlobalSearchQuery('');
        setHighlightedBarcode(barcode);
        handlePalletClick(pallet);
        
        // Clear highlight after 5 seconds
        setTimeout(() => {
            setHighlightedBarcode(null);
        }, 5000);
    };

    const loadPallets = async () => {
        try {
            setLoading(true);
            const res = await api.get('/depot/v2/pallets');
            const palletsData = res.data || [];
            
            const processedPallets = palletsData.map(p => ({
                ...p,
                pallet_id: p.number || p.id,
                total_items: (p.boxes || []).reduce((sum, box) => 
                    sum + (box.items || []).reduce((itemSum, item) => itemSum + (item.quantity || 0), 0)
                , 0)
            }));
            
            setPallets(processedPallets);
        } catch (err) {
            console.error('Error loading pallets:', err);
            toast.error('Eroare la încărcarea paleților');
        } finally {
            setLoading(false);
        }
    };

    const getNextPalletNumber = () => {
        if (pallets.length === 0) return 1;
        const numbers = pallets
            .map(p => parseInt(p.pallet_id || p.number || '0'))
            .filter(n => !isNaN(n));
        return numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
    };

    const handleAddPallet = async () => {
        try {
            const nextNumber = getNextPalletNumber();
            await api.post('/depot/v2/pallets', { number: nextNumber });
            toast.success(`Palet ${nextNumber} creat`);
            loadPallets();
        } catch (err) {
            console.error('Error creating pallet:', err);
            const errorDetail = err.response?.data?.detail;
            if (typeof errorDetail === 'string') {
                toast.error(errorDetail);
            } else if (Array.isArray(errorDetail)) {
                toast.error(errorDetail[0]?.msg || 'Eroare la crearea paletului');
            } else {
                toast.error('Eroare la crearea paletului');
            }
        }
    };

    const handlePalletClick = async (pallet) => {
        setSelectedPallet(pallet);
        setShowPalletModal(true);
        resetScanForm();
        await loadPalletItems(pallet.id);
        
        setTimeout(() => {
            scan1Ref.current?.focus();
            
            // Scroll to highlighted product if any
            if (highlightedBarcode) {
                setTimeout(() => {
                    const highlightedRow = document.querySelector(`tr[data-barcode="${highlightedBarcode}"]`);
                    if (highlightedRow) {
                        highlightedRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 200);
            }
        }, 100);
    };

    const loadPalletItems = async (palletId) => {
        try {
            setLoadingItems(true);
            const res = await api.get(`/depot/v2/pallet/${palletId}/items`);
            setPalletItems(res.data.items || []);
        } catch (err) {
            console.error('Error loading pallet items:', err);
            toast.error('Eroare la încărcarea produselor');
        } finally {
            setLoadingItems(false);
        }
    };

    const resetScanForm = () => {
        setScan1('');
        setScan2('');
        setScanDescription('');
        setScanQty(1);
        setFetchedPrice(0);
        setPriceSource('');
        setManualPrice('');
        setScanError('');
        setShortCodeWarning(false);
        setScanValidated(false);
        setValidatedBorder(false);
        setExistingProduct(null);
        setDescriptionLocked(false);
    };

    // Handle Scan 1 completion
    // Handle Scan 1 - normalize barcode (no spaces, max 16 chars)
    const handleScan1Change = (e) => {
        const value = normalizeBarcode(e.target.value);
        setScan1(value);
        setScanError('');
        setShortCodeWarning(false);
    };

    const handleScan1KeyDown = (e) => {
        if (e.key === 'Enter' && scan1.trim()) {
            e.preventDefault();
            
            // Check for short code warning
            if (scan1.trim().length < 5) {
                setShortCodeWarning(true);
                toast.warning('Cod scurt - verifică eticheta!', { duration: 2000 });
            }
            
            // Move focus to Scan 2
            scan2Ref.current?.focus();
        }
    };

    // Handle Scan 2 completion - validate against Scan 1 (also normalized)
    const handleScan2Change = (e) => {
        const value = normalizeBarcode(e.target.value);
        setScan2(value);
    };

    const handleScan2KeyDown = async (e) => {
        if (e.key === 'Enter' && scan2.trim()) {
            e.preventDefault();
            
            // Validate: Scan1 must equal Scan2
            if (scan1.trim() !== scan2.trim()) {
                // Error - codes don't match
                setScanError('Eroare scanare: codurile nu coincid!');
                setScan1('');
                setScan2('');
                setScanValidated(false);
                
                // Return focus to Scan 1
                setTimeout(() => {
                    scan1Ref.current?.focus();
                }, 100);
                return;
            }
            
            // Success - codes match!
            setScanError('');
            setScanValidated(true);
            setValidatedBorder(true);
            
            // Green border for 1 second
            setTimeout(() => {
                setValidatedBorder(false);
            }, 1000);
            
            // Check if product exists in database
            await checkProductExists(scan1.trim());
        }
    };

    const checkProductExists = async (barcode) => {
        try {
            // Fetch price from Recepție Paleti and product info in parallel
            const [lookupRes, priceRes] = await Promise.all([
                api.get(`/products/lookup/${barcode}`).catch(() => ({ data: { found: false } })),
                api.get(`/depot/v2/product-price/${barcode}`).catch(() => ({ data: { price: 0, source: 'none' } }))
            ]);
            
            // Set fetched price from Recepție
            const fetchedPriceData = priceRes.data || { price: 0, source: 'none' };
            setFetchedPrice(fetchedPriceData.price || 0);
            setPriceSource(fetchedPriceData.source || 'none');
            
            // Fetch min sale price and auto-calculate purchase price as 80%
            let autoPrice = fetchedPriceData.price || 0;
            try {
                const minPriceRes = await api.post('/products/last-prices/batch', { barcodes: [barcode.toUpperCase()] });
                const minSalePrice = minPriceRes.data?.prices?.[barcode.toUpperCase()] || 0;
                if (minSalePrice > 0) {
                    autoPrice = parseFloat((minSalePrice * 0.8).toFixed(2));
                    toast.info(`Preț achiziție auto: ${autoPrice.toFixed(2)} RON (80% din vânzare min ${minSalePrice.toFixed(2)})`);
                }
            } catch (e) {
                console.error('Error fetching min sale price:', e);
            }
            
            setManualPrice(autoPrice > 0 ? autoPrice.toFixed(2) : '');
            
            if (autoPrice > 0 && fetchedPriceData.price <= 0) {
                toast.info(`Preț achiziție preluat: ${autoPrice.toFixed(2)} RON`);
            } else if (fetchedPriceData.price > 0 && autoPrice === fetchedPriceData.price) {
                toast.info(`Preț mediu preluat: ${fetchedPriceData.price.toFixed(2)} RON (${fetchedPriceData.source === 'receptie_paleti' ? 'Recepție' : 'Istoric'})`);
            }
            
            if (lookupRes.data?.found && lookupRes.data?.product) {
                const product = lookupRes.data.product;
                // Product exists - auto-fill description but allow editing
                setExistingProduct(product);
                setScanDescription(product.name || '');
                setDescriptionLocked(false);
                
                toast.success(`Produs găsit: ${product.name}`);
                
                // Move focus to quantity
                setTimeout(() => {
                    quantityRef.current?.focus();
                    quantityRef.current?.select();
                }, 100);
            } else {
                // Product doesn't exist - allow manual description entry
                setExistingProduct(null);
                setDescriptionLocked(false);
                
                // Move focus to description
                setTimeout(() => {
                    descriptionRef.current?.focus();
                }, 100);
            }
        } catch (err) {
            console.error('Error checking product:', err);
            // On error, allow manual entry
            setExistingProduct(null);
            setDescriptionLocked(false);
            setFetchedPrice(0);
            setPriceSource('none');
            setManualPrice('');
            setTimeout(() => {
                descriptionRef.current?.focus();
            }, 100);
        }
    };

    // Handle description entry for new products
    const handleDescriptionKeyDown = (e) => {
        if (e.key === 'Enter' && scanDescription.trim()) {
            e.preventDefault();
            // Move to quantity field
            quantityRef.current?.focus();
            quantityRef.current?.select();
        }
    };

    // Handle quantity entry - move to price field
    const handleQuantityKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            priceRef.current?.focus();
            priceRef.current?.select();
        }
    };

    // Handle price entry - submit on Enter
    const handlePriceKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmitScan();
        }
    };

    const handleSubmitScan = async () => {
        if (!scanValidated || !scan1.trim() || !selectedPallet) return;
        
        if (!scanDescription.trim()) {
            toast.error('Introduceți descrierea produsului');
            descriptionRef.current?.focus();
            return;
        }
        
        // Block QTY > 100
        if (scanQty > 100) {
            toast.error('Cantitatea nu poate depăși 100');
            quantityRef.current?.focus();
            return;
        }
        
        // Block price > 100 — ask confirmation
        const finalPriceCheck = manualPrice !== '' ? parseFloat(manualPrice) : 0;
        if (finalPriceCheck > 100) {
            setConfirmHighValue({ type: 'price', value: finalPriceCheck });
            return;
        }
        
        await doSubmitScan();
    };
    
    const doSubmitScan = async () => {
        if (!scanValidated || !scan1.trim() || !selectedPallet) return;
        setConfirmHighValue(null);
        
        try {
            setScanning(true);
            
            // Check if description needs suffix (_1, _2, etc.)
            let finalDescription = scanDescription.trim();
            if (!existingProduct) {
                finalDescription = await getUniqueDescription(finalDescription, scan1.trim());
            }
            
            // If user changed the name of an existing product, update it everywhere
            const nameChanged = existingProduct && finalDescription !== existingProduct.name;
            
            const finalPrice = manualPrice !== '' ? parseFloat(manualPrice) : undefined;
            
            const res = await api.post(`/depot/v2/pallet/${selectedPallet.id}/scan`, {
                pallet_id: selectedPallet.id,
                barcode: scan1.trim(),
                name: finalDescription,
                quantity: scanQty,
                purchase_price: finalPrice !== undefined && !isNaN(finalPrice) ? finalPrice : undefined
            });
            
            const message = res.data.transfer_from_store 
                ? `✓ Transferat din magazin: ${finalDescription}`
                : `✓ Produs adăugat: ${finalDescription}`;
            
            // Show price info if available
            if (res.data.purchase_price > 0) {
                toast.success(`${message} (Preț: ${res.data.purchase_price.toFixed(2)} RON)`);
            } else {
                toast.success(message);
            }
            
            // Reload items
            await loadPalletItems(selectedPallet.id);
            await loadPallets();
            
            // If name was changed, update it everywhere
            if (nameChanged) {
                try {
                    await api.post('/products/v2/update-name', {
                        barcode: scan1.trim(),
                        name: finalDescription
                    });
                    toast.info(`Denumire actualizată peste tot: ${finalDescription}`);
                } catch (e) {
                    console.error('Name update error:', e);
                }
            }
            
            // Reset form and focus on Scan 1
            resetScanForm();
            setTimeout(() => {
                scan1Ref.current?.focus();
            }, 100);
            
        } catch (err) {
            console.error('Error scanning:', err);
            toast.error(err.response?.data?.detail || 'Eroare la adăugare');
        } finally {
            setScanning(false);
        }
    };

    // Get unique description with suffix if needed
    const getUniqueDescription = async (description, barcode) => {
        try {
            const res = await api.get(`/products/v2/check-name?name=${encodeURIComponent(description)}&barcode=${barcode}`);
            return res.data.unique_name || description;
        } catch (err) {
            // If endpoint doesn't exist, check locally
            const existingNames = palletItems
                .filter(item => item.barcode !== barcode)
                .map(item => item.name?.toLowerCase());
            
            if (!existingNames.includes(description.toLowerCase())) {
                return description;
            }
            
            // Add suffix
            let suffix = 1;
            let uniqueName = `${description}_${suffix}`;
            while (existingNames.includes(uniqueName.toLowerCase())) {
                suffix++;
                uniqueName = `${description}_${suffix}`;
            }
            return uniqueName;
        }
    };

    // Edit product name inline
    const startEditName = (barcode, currentName) => {
        setEditingBarcode(barcode);
        setEditNameValue(currentName || '');
        setTimeout(() => editNameRef.current?.focus(), 50);
    };

    const saveEditName = async () => {
        if (!editingBarcode || !editNameValue.trim()) {
            setEditingBarcode(null);
            return;
        }
        try {
            await api.post('/products/v2/update-name', {
                barcode: editingBarcode,
                name: editNameValue.trim()
            });
            toast.success('Denumire actualizată peste tot');
            if (selectedPallet) {
                await loadPalletItems(selectedPallet.id);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la actualizare');
        }
        setEditingBarcode(null);
    };

    // Edit price inline in table
    const startEditPrice = (barcode, currentPrice) => {
        setEditingPriceBarcode(barcode);
        setEditPriceValue(currentPrice > 0 ? currentPrice.toFixed(2) : '');
        setTimeout(() => editPriceRef.current?.focus(), 50);
    };

    const saveEditPrice = async () => {
        if (!editingPriceBarcode) {
            setEditingPriceBarcode(null);
            return;
        }
        const newPrice = parseFloat(editPriceValue);
        if (isNaN(newPrice) || newPrice < 0) {
            setEditingPriceBarcode(null);
            return;
        }
        
        // Block price > 100 with confirmation
        if (newPrice > 100) {
            setConfirmHighValue({ 
                type: 'editPrice', 
                value: newPrice, 
                barcode: editingPriceBarcode 
            });
            return;
        }
        
        await doSaveEditPrice(editingPriceBarcode, newPrice);
    };

    const doSaveEditPrice = async (barcode, price) => {
        setConfirmHighValue(null);
        try {
            await api.post('/depot/v2/update-price', {
                barcode: barcode,
                price: price
            });
            toast.success(`Preț actualizat: ${price.toFixed(2)} RON`);
            if (selectedPallet) {
                await loadPalletItems(selectedPallet.id);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la actualizare preț');
        }
        setEditingPriceBarcode(null);
    };

    // Move items to another pallet
    const handleMoveItems = async () => {
        if (!selectedPallet || !moveTargetPallet) return;
        setMoveLoading(true);
        try {
            const res = await api.post(`/depot/v2/pallet/${selectedPallet.id}/move-items`, {
                target_pallet_id: moveTargetPallet,
                item_ids: selectedMoveItems.length > 0 ? selectedMoveItems : 'all'
            });
            toast.success(res.data.message);
            setShowMoveDialog(false);
            setMoveTargetPallet('');
            setSelectedMoveItems([]);
            await loadPalletItems(selectedPallet.id);
            await loadPallets();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la mutare');
        }
        setMoveLoading(false);
    };

    const handleExportExcel = async () => {
        if (!selectedPallet) return;
        try {
            const response = await api.get(`/depot/v2/pallet/${selectedPallet.id}/export/excel`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `palet_${selectedPallet.number || selectedPallet.pallet_id}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Excel descărcat');
        } catch (err) {
            console.error('Error exporting:', err);
            toast.error('Eroare la export');
        }
    };

    const handleExportPDF = async () => {
        if (!selectedPallet) return;
        try {
            const response = await api.get(`/depot/v2/pallet/${selectedPallet.id}/export/pdf`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `palet_${selectedPallet.number || selectedPallet.pallet_id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('PDF descărcat');
        } catch (err) {
            console.error('Error exporting PDF:', err);
            toast.error('Eroare la export PDF');
        }
    };

    const filteredPallets = useMemo(() => {
        if (!searchQuery.trim()) return pallets;
        const query = searchQuery.toLowerCase();
        return pallets.filter(p => {
            const palletId = String(p.pallet_id || p.number || '').toLowerCase();
            const boxes = p.boxes || [];
            const hasMatchingItem = boxes.some(box => 
                (box.items || []).some(item =>
                    (item.name || '').toLowerCase().includes(query) ||
                    (item.barcode || '').toLowerCase().includes(query)
                )
            );
            return palletId.includes(query) || hasMatchingItem;
        });
    }, [pallets, searchQuery]);

    const getPalletTotal = (pallet) => pallet.total_items || 0;

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            return new Date(dateStr).toLocaleDateString('ro-RO');
        } catch {
            return '-';
        }
    };

    // Get border class based on validation state
    const getScanBorderClass = () => {
        if (validatedBorder) return 'border-green-500 ring-2 ring-green-500/50';
        if (scanError) return 'border-red-500';
        return 'border-slate-600';
    };

    return (
        <div className="min-h-screen bg-slate-950 p-4">
            {/* Header with Global Search */}
            <div className="mb-6">
                <div className="flex items-center gap-4 mb-4">
                    <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                        <Package className="w-7 h-7 text-amber-500" />
                        Depozit
                    </h1>
                    <DepotLockToggle />
                    
                    {/* Global Search Bar - Compact, Left Side */}
                    <div ref={searchRef} className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            type="text"
                            placeholder="Caută produs sau cod..."
                            value={globalSearchQuery}
                            onChange={(e) => setGlobalSearchQuery(e.target.value)}
                            onFocus={() => globalSearchResults.length > 0 && setShowSearchResults(true)}
                            className="pl-9 pr-8 h-9 text-sm bg-slate-900 border-slate-700 text-white placeholder:text-slate-500"
                            data-testid="global-search-input"
                        />
                        {globalSearchQuery && (
                            <button
                                onClick={() => {
                                    setGlobalSearchQuery('');
                                    setShowSearchResults(false);
                                }}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                        
                        {/* Search Results Dropdown */}
                        {showSearchResults && globalSearchResults.length > 0 && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
                                {globalSearchResults.map((product, idx) => (
                                    <div 
                                        key={product.barcode}
                                        className={`p-3 hover:bg-slate-700/50 ${idx !== globalSearchResults.length - 1 ? 'border-b border-slate-700' : ''}`}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-white font-medium truncate">{product.name}</p>
                                                <p className="text-xs text-slate-400 font-mono">{product.barcode}</p>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <p className="text-xs text-slate-500">
                                                    {product.locations.reduce((sum, l) => sum + l.quantity, 0)} buc total
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5 mt-2">
                                            {product.locations.map((loc) => (
                                                <button
                                                    key={loc.palletNumber}
                                                    onClick={() => navigateToPallet(loc.pallet, product.barcode)}
                                                    className="px-2 py-1 text-xs bg-amber-600/20 text-amber-400 rounded hover:bg-amber-600/40 transition-colors flex items-center gap-1"
                                                >
                                                    <Package className="w-3 h-3" />
                                                    Palet {loc.palletNumber}: {loc.quantity} buc
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        {/* No Results */}
                        {showSearchResults && globalSearchQuery && globalSearchResults.length === 0 && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl z-50 p-4 text-center">
                                <p className="text-slate-400 text-sm">Niciun produs găsit pentru "{globalSearchQuery}"</p>
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Pallet Filter (Legacy search - hidden but functional) */}
                <div className="relative hidden">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                        type="text"
                        placeholder="Caută palet sau produs..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500"
                        data-testid="depot-search-input"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>

            {/* Loading State */}
            {loading ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                    {[...Array(12)].map((_, i) => (
                        <Skeleton key={i} className="aspect-square rounded-xl bg-slate-800" />
                    ))}
                </div>
            ) : (
                /* Pallet Grid */
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                    {/* Add Pallet Button */}
                    <button
                        onClick={handleAddPallet}
                        className="aspect-square rounded-xl bg-gradient-to-br from-amber-600 to-amber-700 
                                   hover:from-amber-500 hover:to-amber-600 transition-all duration-200
                                   flex flex-col items-center justify-center gap-2 shadow-lg
                                   hover:shadow-amber-500/20 hover:scale-[1.02] active:scale-[0.98]"
                        data-testid="add-pallet-btn"
                    >
                        <Plus className="w-10 h-10 text-white" />
                        <span className="text-white text-sm font-medium">Adaugă Palet</span>
                    </button>

                    {/* Pallet Cards */}
                    {filteredPallets.map((pallet) => (
                        <button
                            key={pallet.pallet_id || pallet._id}
                            onClick={() => handlePalletClick(pallet)}
                            className="aspect-square rounded-xl bg-slate-800 border border-slate-700
                                       hover:border-amber-500/50 hover:bg-slate-750 transition-all duration-200
                                       flex flex-col items-center justify-center gap-2 shadow-lg
                                       hover:shadow-amber-500/10 hover:scale-[1.02] active:scale-[0.98]"
                            data-testid={`pallet-card-${pallet.pallet_id}`}
                        >
                            <Package className="w-8 h-8 text-amber-500" />
                            <span className="text-white text-lg font-bold">
                                {pallet.pallet_id || pallet.number}
                            </span>
                            <span className="text-slate-400 text-xs">
                                {getPalletTotal(pallet)} buc
                            </span>
                        </button>
                    ))}

                    {/* Empty State */}
                    {filteredPallets.length === 0 && !loading && (
                        <div className="col-span-full text-center py-12 text-slate-500">
                            {searchQuery ? (
                                <p>Niciun palet găsit pentru "{searchQuery}"</p>
                            ) : (
                                <p>Niciun palet în depozit. Apasă + pentru a adăuga.</p>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Pallet Modal */}
            <Dialog open={showPalletModal} onOpenChange={setShowPalletModal}>
                <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-xl">
                            <Package className="w-6 h-6 text-amber-500" />
                            Palet {selectedPallet?.pallet_id || selectedPallet?.number}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="flex flex-col flex-1 overflow-hidden">
                        {/* Scan Section - FIXED/STICKY */}
                        <div className="flex-shrink-0 bg-slate-800/50 rounded-lg p-4 space-y-3">
                            <div className="flex items-center gap-2 text-amber-500 font-medium">
                                <Scan className="w-5 h-5" />
                                Scanare Dublă (Verificare)
                            </div>
                            
                            {/* Error Alert */}
                            {scanError && (
                                <div className="flex items-center gap-2 p-3 bg-red-900/30 border border-red-500 rounded-lg text-red-400">
                                    <XCircle className="w-5 h-5 flex-shrink-0" />
                                    <span className="font-medium">{scanError}</span>
                                </div>
                            )}
                            
                            {/* Short Code Warning */}
                            {shortCodeWarning && !scanError && (
                                <div className="flex items-center gap-2 p-2 bg-yellow-900/30 border border-yellow-500/50 rounded-lg text-yellow-400 text-sm">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                    <span>Cod scurt - verifică eticheta!</span>
                                </div>
                            )}
                            
                            {/* Validation Success */}
                            {scanValidated && !scanError && (
                                <div className="flex items-center gap-2 p-2 bg-green-900/30 border border-green-500/50 rounded-lg text-green-400 text-sm">
                                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                                    <span>Cod validat: {scan1}</span>
                                </div>
                            )}
                            
                            {/* Scan Fields */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-slate-400 text-xs">Scanare 1 *</Label>
                                    <Input
                                        ref={scan1Ref}
                                        type="text"
                                        placeholder="Scanează codul..."
                                        value={scan1}
                                        onChange={handleScan1Change}
                                        onKeyDown={handleScan1KeyDown}
                                        disabled={scanValidated}
                                        className={`bg-slate-900 text-white transition-all ${getScanBorderClass()} ${scanValidated ? 'opacity-60' : ''}`}
                                        data-testid="scan1-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-slate-400 text-xs">Scanare 2 (verificare) *</Label>
                                    <Input
                                        ref={scan2Ref}
                                        type="text"
                                        placeholder="Scanează din nou..."
                                        value={scan2}
                                        onChange={handleScan2Change}
                                        onKeyDown={handleScan2KeyDown}
                                        disabled={scanValidated || !scan1.trim()}
                                        className={`bg-slate-900 text-white transition-all ${getScanBorderClass()} ${scanValidated ? 'opacity-60' : ''} ${!scan1.trim() ? 'opacity-40' : ''}`}
                                        data-testid="scan2-input"
                                    />
                                </div>
                            </div>
                            
                            {/* Description & Quantity (shown after validation) */}
                            {scanValidated && (
                                <>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label className="text-slate-400 text-xs">
                                                Descriere {existingProduct ? '(auto-completat)' : '*'}
                                            </Label>
                                            <Input
                                                ref={descriptionRef}
                                                type="text"
                                                placeholder="Descriere produs..."
                                                value={scanDescription}
                                                onChange={(e) => setScanDescription(e.target.value)}
                                                onKeyDown={handleDescriptionKeyDown}
                                                disabled={descriptionLocked}
                                                className={`bg-slate-900 border-slate-600 text-white ${descriptionLocked ? 'bg-slate-800 text-green-400' : ''}`}
                                                data-testid="description-input"
                                            />
                                            {!existingProduct && (
                                                <p className="text-xs text-slate-500 mt-1">
                                                    Dacă descrierea există, se va adăuga sufix automat (_1, _2...)
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <Label className="text-slate-400 text-xs">Cantitate</Label>
                                            <Input
                                                ref={quantityRef}
                                                type="number"
                                                min="1"
                                                max="100"
                                                value={scanQty}
                                                onChange={(e) => {
                                                    const val = parseInt(e.target.value) || 1;
                                                    setScanQty(Math.min(val, 100));
                                                }}
                                                onKeyDown={handleQuantityKeyDown}
                                                className={`bg-slate-900 border-slate-600 text-white ${scanQty >= 100 ? 'border-amber-500' : ''}`}
                                                data-testid="quantity-input"
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label className="text-slate-400 text-xs">
                                                Preț Achiziție (RON)
                                                {priceSource && priceSource !== 'none' && (
                                                    <span className="ml-1 text-slate-500">
                                                        ({priceSource === 'receptie_paleti' ? 'Recepție' : priceSource === 'depot_history' ? 'Istoric' : 'Catalog'})
                                                    </span>
                                                )}
                                            </Label>
                                            <Input
                                                ref={priceRef}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                placeholder="0.00"
                                                value={manualPrice}
                                                onChange={(e) => setManualPrice(e.target.value)}
                                                onKeyDown={handlePriceKeyDown}
                                                className={`bg-slate-900 text-white ${manualPrice && parseFloat(manualPrice) !== fetchedPrice ? 'border-amber-500 text-amber-300' : 'border-slate-600'}`}
                                                data-testid="price-input"
                                            />
                                            {manualPrice && parseFloat(manualPrice) !== fetchedPrice && fetchedPrice > 0 && (
                                                <p className="text-xs text-amber-400 mt-1">
                                                    Preț modificat manual (propus: {fetchedPrice.toFixed(2)})
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex items-end gap-2">
                                            <Button 
                                                type="button"
                                                onClick={handleSubmitScan}
                                                disabled={!scanDescription.trim() || scanning}
                                                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                                                data-testid="submit-scan-btn"
                                            >
                                                {scanning ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <>
                                                        <CheckCircle2 className="w-4 h-4 mr-1" />
                                                        Adaugă pe Palet
                                                    </>
                                                )}
                                            </Button>
                                            <Button 
                                                type="button"
                                                variant="outline"
                                                onClick={resetScanForm}
                                                className="border-slate-600 text-slate-300 hover:bg-slate-800"
                                            >
                                                <X className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </>
                            )}
                            
                            <p className="text-xs text-slate-500 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                Scanează același cod de două ori pentru validare. Enter pentru a avansa.
                            </p>
                        </div>

                        {/* Items Table - SCROLLABLE */}
                        <div className="flex-1 overflow-y-auto mt-3">
                        <div className="bg-slate-800/30 rounded-lg overflow-hidden">
                            <div className="p-3 border-b border-slate-700 flex items-center justify-between">
                                <span className="text-slate-300 font-medium">
                                    Produse ({palletItems.length})
                                </span>
                                <div className="flex gap-2">
                                    <Button 
                                        size="sm" 
                                        variant="outline"
                                        onClick={() => setShowMoveDialog(true)}
                                        disabled={palletItems.length === 0}
                                        className="border-amber-600 text-amber-400 hover:bg-amber-900/30"
                                        data-testid="move-items-btn"
                                    >
                                        <MoveRight className="w-4 h-4 mr-1" />
                                        Mută{selectedMoveItems.length > 0 ? ` (${selectedMoveItems.length})` : ' tot'}
                                    </Button>
                                    <Button 
                                        size="sm" 
                                        variant="outline"
                                        onClick={handleExportExcel}
                                        disabled={palletItems.length === 0}
                                        className="border-slate-600 text-slate-300 hover:bg-slate-700"
                                    >
                                        <FileSpreadsheet className="w-4 h-4 mr-1" />
                                        Excel
                                    </Button>
                                    <Button 
                                        size="sm" 
                                        variant="outline"
                                        onClick={handleExportPDF}
                                        disabled={palletItems.length === 0}
                                        className="border-slate-600 text-slate-300 hover:bg-slate-700"
                                    >
                                        <Printer className="w-4 h-4 mr-1" />
                                        PDF
                                    </Button>
                                </div>
                            </div>
                            
                            {loadingItems ? (
                                <div className="p-8 text-center">
                                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-500" />
                                </div>
                            ) : palletItems.length === 0 ? (
                                <div className="p-8 text-center text-slate-500">
                                    <Package className="w-12 h-12 mx-auto mb-2 opacity-30" />
                                    <p>Niciun produs pe acest palet</p>
                                    <p className="text-xs mt-1">Scanează un cod de bare pentru a adăuga</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-slate-800">
                                            <tr>
                                                <th className="p-2 w-8">
                                                    <input 
                                                        type="checkbox"
                                                        checked={selectedMoveItems.length === palletItems.length && palletItems.length > 0}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedMoveItems(palletItems.map(i => i.id));
                                                            } else {
                                                                setSelectedMoveItems([]);
                                                            }
                                                        }}
                                                        className="accent-amber-500 w-3.5 h-3.5 cursor-pointer"
                                                        data-testid="select-all-checkbox"
                                                    />
                                                </th>
                                                <th className="text-left p-2 text-slate-400 font-medium text-xs">Produs</th>
                                                <th className="text-right p-2 text-slate-400 font-medium text-xs">Cant.</th>
                                                <th className="text-right p-2 text-slate-400 font-medium text-xs">Data Intrare Stoc</th>
                                                <th className="text-right p-2 text-slate-400 font-medium text-xs">Preț Mediu (Min)</th>
                                                <th className="text-right p-2 text-slate-400 font-medium text-xs">Val. Stoc</th>
                                                <th className="text-right p-2 text-slate-400 font-medium text-xs">Preț Vânz. (Min)</th>
                                                <th className="text-right p-2 text-slate-400 font-medium text-xs">Val. Estimată</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {palletItems.map((item) => (
                                                <tr 
                                                    key={item.id} 
                                                    className={`border-t border-slate-700/50 hover:bg-slate-800/50 transition-colors ${
                                                        highlightedBarcode && item.barcode?.toUpperCase() === highlightedBarcode?.toUpperCase()
                                                            ? 'bg-amber-600/30 ring-1 ring-amber-500/50'
                                                            : ''
                                                    }`}
                                                    data-barcode={item.barcode}
                                                >
                                                    <td className="p-2 w-8">
                                                        <input 
                                                            type="checkbox"
                                                            checked={selectedMoveItems.includes(item.id)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedMoveItems(prev => [...prev, item.id]);
                                                                } else {
                                                                    setSelectedMoveItems(prev => prev.filter(id => id !== item.id));
                                                                }
                                                            }}
                                                            className="accent-amber-500 w-3.5 h-3.5 cursor-pointer"
                                                        />
                                                    </td>
                                                    <td className="p-2">
                                                        {editingBarcode === item.barcode ? (
                                                            <input
                                                                ref={editNameRef}
                                                                type="text"
                                                                value={editNameValue}
                                                                onChange={(e) => setEditNameValue(e.target.value)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') saveEditName();
                                                                    if (e.key === 'Escape') setEditingBarcode(null);
                                                                }}
                                                                onBlur={saveEditName}
                                                                className="w-full bg-slate-700 text-white text-sm px-2 py-1 rounded border border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-400"
                                                                data-testid="edit-name-input"
                                                            />
                                                        ) : (
                                                            <div className="group flex items-center gap-1 cursor-pointer" onClick={() => startEditName(item.barcode, item.name)}>
                                                                <div className={`text-sm ${highlightedBarcode && item.barcode?.toUpperCase() === highlightedBarcode?.toUpperCase() ? 'text-amber-300 font-semibold' : 'text-white'}`}>{item.name || '-'}</div>
                                                                <Pencil size={12} className="text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                                                            </div>
                                                        )}
                                                        <div className="text-xs text-slate-500">{item.barcode}</div>
                                                    </td>
                                                    <td className="p-2 text-right text-white">{item.quantity || 0}</td>
                                                    <td className="p-2 text-right text-slate-300 text-xs">
                                                        {item.entry_timestamp ? (
                                                            <span>{new Date(item.entry_timestamp).toLocaleDateString('ro-RO')} {new Date(item.entry_timestamp).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}</span>
                                                        ) : (
                                                            <span className="text-slate-500 italic">-</span>
                                                        )}
                                                    </td>
                                                    <td className="p-2 text-right text-slate-300">
                                                        {(item.pret_mediu_min || 0) > 0 ? (
                                                            <span>{(item.pret_mediu_min || 0).toFixed(2)}</span>
                                                        ) : (
                                                            <span className="text-slate-500 italic">-</span>
                                                        )}
                                                    </td>
                                                    <td className="p-2 text-right text-amber-400 font-medium">
                                                        {(item.valoare_stoc || 0) > 0 ? (
                                                            <span>{(item.valoare_stoc || 0).toFixed(2)}</span>
                                                        ) : (
                                                            <span className="text-slate-500">-</span>
                                                        )}
                                                    </td>
                                                    <td className="p-2 text-right text-green-400">
                                                        {editingPriceBarcode === item.barcode ? (
                                                            <input
                                                                ref={editPriceRef}
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                value={editPriceValue}
                                                                onChange={(e) => setEditPriceValue(e.target.value)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') saveEditPrice();
                                                                    if (e.key === 'Escape') setEditingPriceBarcode(null);
                                                                }}
                                                                onBlur={saveEditPrice}
                                                                className="w-20 bg-slate-700 text-green-400 text-sm text-right px-2 py-1 rounded border border-green-500 focus:outline-none focus:ring-1 focus:ring-green-400"
                                                                data-testid="edit-price-input"
                                                            />
                                                        ) : (
                                                            <div className="group flex items-center justify-end gap-1 cursor-pointer" onClick={() => startEditPrice(item.barcode, item.pret_vanzare_min || 0)}>
                                                                {(item.pret_vanzare_min || 0) > 0 ? (
                                                                    <span>{(item.pret_vanzare_min || 0).toFixed(2)}</span>
                                                                ) : (
                                                                    <span className="text-slate-500 italic">-</span>
                                                                )}
                                                                <Pencil size={10} className="text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="p-2 text-right text-green-500 font-medium">
                                                        {(item.valoare_estimata || 0) > 0 ? (
                                                            <span>{(item.valoare_estimata || 0).toFixed(2)}</span>
                                                        ) : (
                                                            <span className="text-slate-500">-</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="bg-slate-800/50">
                                            <tr className="border-t border-slate-600">
                                                <td className="p-2 font-bold text-white">TOTAL</td>
                                                <td className="p-2 text-right font-bold text-white">
                                                    {palletItems.reduce((sum, i) => sum + (i.quantity || 0), 0)}
                                                </td>
                                                <td className="p-2"></td>
                                                <td className="p-2"></td>
                                                <td className="p-2 text-right font-bold text-amber-400">
                                                    {palletItems.reduce((sum, i) => sum + (i.valoare_stoc || 0), 0).toFixed(2)}
                                                </td>
                                                <td className="p-2"></td>
                                                <td className="p-2 text-right font-bold text-green-500">
                                                    {palletItems.reduce((sum, i) => sum + (i.valoare_estimata || 0), 0).toFixed(2)}
                                                </td>
                                            </tr>
                                            {palletItems.reduce((sum, i) => sum + (i.valoare_estimata || 0), 0) > 0 && (
                                                <tr className="border-t border-slate-600/50">
                                                    <td colSpan="6" className="p-2 text-right text-slate-400">Profit Estimat:</td>
                                                    <td className={`p-2 text-right font-bold ${(palletItems.reduce((sum, i) => sum + (i.valoare_estimata || 0), 0) - palletItems.reduce((sum, i) => sum + (i.valoare_stoc || 0), 0)) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                        {(palletItems.reduce((sum, i) => sum + (i.valoare_estimata || 0), 0) - palletItems.reduce((sum, i) => sum + (i.valoare_stoc || 0), 0)).toFixed(2)} RON
                                                    </td>
                                                </tr>
                                            )}
                                        </tfoot>
                                    </table>
                                </div>
                            )}
                        </div>
                        </div> {/* end scrollable area */}
                    </div>

                    <div className="flex items-start justify-between pt-4 border-t border-slate-700 mt-4 gap-4">
                        {/* Ultimele 3 produse adăugate */}
                        {palletItems.length > 0 && (
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-slate-500 mb-1.5">Ultimele adăugate:</p>
                                <div className="space-y-1">
                                    {[...palletItems]
                                        .sort((a, b) => (b.entry_timestamp || b.created_at || '').localeCompare(a.entry_timestamp || a.created_at || ''))
                                        .slice(0, 3)
                                        .map((item, idx) => (
                                            <div key={idx} className="flex items-center gap-2 text-xs">
                                                <span className="text-amber-500 font-mono w-4 text-right">{idx + 1}.</span>
                                                <span className="text-slate-300 truncate max-w-[180px]">{item.name || item.barcode}</span>
                                                <span className="text-slate-500">x{item.quantity}</span>
                                                {item.pret_vanzare_min > 0 && (
                                                    <span className="text-green-500">{item.pret_vanzare_min.toFixed(2)}</span>
                                                )}
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        )}
                        <Button
                            variant="outline"
                            onClick={() => setShowPalletModal(false)}
                            className="border-slate-600 text-slate-300 hover:bg-slate-800 flex-shrink-0"
                        >
                            Închide
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dialog: Mută produse pe alt palet */}
            <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
                <DialogContent className="bg-slate-900 border-amber-500 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-amber-400 flex items-center gap-2">
                            <MoveRight size={20} />
                            Mută produse pe alt palet
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-slate-400 text-sm">
                        {selectedMoveItems.length > 0 
                            ? `Se vor muta ${selectedMoveItems.length} produse selectate` 
                            : `Se vor muta TOATE cele ${palletItems.length} produse`}
                        {' '}de pe paletul #{selectedPallet?.number}
                    </p>
                    
                    <div className="space-y-3 mt-2">
                        <Label className="text-slate-300">Palet destinație:</Label>
                        <select
                            value={moveTargetPallet}
                            onChange={(e) => setMoveTargetPallet(e.target.value)}
                            className="w-full bg-slate-800 border border-slate-600 text-white rounded-md px-3 py-2 text-sm focus:ring-amber-500 focus:border-amber-500"
                            data-testid="move-target-select"
                        >
                            <option value="">-- Selectează palet --</option>
                            {pallets.filter(p => p.id !== selectedPallet?.id).map(p => (
                                <option key={p.id} value={p.id}>
                                    Palet #{p.number} ({p.items_count || 0} buc)
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" onClick={() => setShowMoveDialog(false)} className="border-slate-600 text-slate-300">
                            Anulează
                        </Button>
                        <Button 
                            onClick={handleMoveItems} 
                            disabled={!moveTargetPallet || moveLoading}
                            className="bg-amber-600 hover:bg-amber-700"
                            data-testid="confirm-move-btn"
                        >
                            {moveLoading ? <Loader2 className="animate-spin mr-1" size={14} /> : <MoveRight size={14} className="mr-1" />}
                            Mută produsele
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Confirmation dialog for high values */}
            <Dialog open={!!confirmHighValue} onOpenChange={() => setConfirmHighValue(null)}>
                <DialogContent className="bg-slate-900 border-amber-500 max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="text-amber-400">Confirmare valoare mare</DialogTitle>
                    </DialogHeader>
                    <p className="text-slate-300">
                        {confirmHighValue?.type === 'price' && (
                            <>Prețul introdus este <strong className="text-amber-300">{confirmHighValue?.value?.toFixed(2)} RON</strong>. Ești sigur?</>
                        )}
                        {confirmHighValue?.type === 'editPrice' && (
                            <>Prețul introdus pentru <strong className="text-amber-300">{confirmHighValue?.barcode}</strong> este <strong className="text-amber-300">{confirmHighValue?.value?.toFixed(2)} RON</strong>. Ești sigur?</>
                        )}
                    </p>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" onClick={() => setConfirmHighValue(null)} className="border-slate-600 text-slate-300">
                            Anulează
                        </Button>
                        <Button 
                            className="bg-amber-600 hover:bg-amber-700"
                            onClick={() => {
                                if (confirmHighValue?.type === 'price') {
                                    doSubmitScan();
                                } else if (confirmHighValue?.type === 'editPrice') {
                                    doSaveEditPrice(confirmHighValue.barcode, confirmHighValue.value);
                                }
                            }}
                        >
                            Da, salvează
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default DepozitV2;
